// Enhanced invoicing types with unified API features

// Adjustment line on an invoice (discount, deduction, surcharge, etc.)
export interface AdjustmentLine {
  id?: number;
  line_type: 'discount' | 'deduction' | 'surcharge' | 'adjustment';
  description: string;
  amount: number | string; // positive = adds, negative = subtracts
  percentage?: number | string | null;
  show_on_invoice?: boolean;
  order?: number;
}

// Unified invoice type that represents all invoice types (invoice, credit_note, downpayment)
export interface UnifiedInvoice {
  id: number;
  invoice_number: string;
  invoice_type: 'invoice' | 'credit_note' | 'downpayment';
  client: number;
  client_id?: number;
  matter: number;
  matter_id?: number;
  issue_date: string;
  due_date?: string;
  amount?: number;
  description?: string;
  subtotal?: number | string;
  costs_share?: number | string;
  vat_type?: string;
  vat_rate?: number | string;
  vat_amount?: number | string;
  service_charge?: number | string;
  fee_split_percentage?: number;
  fee_split_amount?: number | string;
  total: number | string;
  amount_paid?: number | string;
  display_amount: number; // negative for credit notes
  status: InvoiceStatus | CreditNoteStatus | DownPaymentStatus;
  
  // Boolean flags for easy type checking
  is_credit_note: boolean;
  is_downpayment: boolean;
  
  // Invoice-specific fields
  timesheets?: Timesheet[];
  expenses?: Expense[];
  costs?: any[];
  balance_due?: number | string;
  applied_down_payments_amount?: number | string;
  applied_credit_notes_amount?: number | string;
  
  // Credit note specific fields
  original_invoice_id?: number;
  credit_reason?: string;
  applied_amount?: number | string;
  remaining_amount?: number | string;
  
  // Down payment specific fields
  payment_date?: string;
  
  // Common fields
  notes?: string;
  document_file?: string;
  send_email_notification?: boolean;
  email_sent_at?: string;
  created_at: string;
  updated_at: string;
  
  // Standalone invoice deduction
  deduct_from_next_invoice?: boolean;
  deducted_amount?: number | string;

  // Discount & adjustment fields
  discount_type?: 'none' | 'percentage' | 'fixed' | 'both';
  discount_percentage?: number | string;
  discount_amount?: number | string;
  discount_description?: string;
  show_discount_on_invoice?: boolean;
  flat_fee_adjustment?: number | string;
  flat_fee_description?: string;
  show_flat_fee_on_invoice?: boolean;
  hour_adjustment?: number | string;
  rate_adjustment?: number | string;
  hour_adjustment_description?: string;
  show_hour_adjustment_on_invoice?: boolean;
  target_amount?: number | string | null;
  fixed_deduction_amount?: number | string;
  fixed_deduction_description?: string;
  show_fixed_deduction_on_invoice?: boolean;
  detail_level?: 'short' | 'summary' | 'detailed' | '';

  // Structured adjustment lines
  adjustment_lines?: AdjustmentLine[];

  // Display names (populated by API)
  client_name?: string;
  matter_name?: string;
  
  // Original invoice (for credit notes)
  original_invoice?: number | null;
}

// Legacy Invoice interface for backwards compatibility
export interface Invoice {
  id: number;
  invoice_number: string;
  client: number;
  client_id: number;
  matter: number;
  matter_id: number;
  issue_date: string;
  due_date: string;
  subtotal: number | string;
  vat_type: string;
  vat_rate: number | string;
  vat_amount: number | string;
  total: number | string;
  status: InvoiceStatus;
  timesheets: Timesheet[];
  expenses: Expense[];
  costs: any[];
  notes?: string;
  document_file?: string;
  send_email_notification?: boolean;
  email_sent_at?: string;
  created_at: string;
  updated_at: string;
  balance_due?: number | string;
  applied_down_payments_amount?: number | string;
  applied_credit_notes_amount?: number | string;
}

export interface CreditNote {
  id: number;
  credit_note_number: string;
  client: number;
  matter: number;
  original_invoice?: number | null;
  issue_date: string;
  amount: number | string;
  vat_type?: string;
  vat_rate?: number | string;
  vat_amount?: number | string;
  total?: number | string;
  applied_amount: number | string;
  remaining_amount: number | string;
  status: CreditNoteStatus;
  reason: string;
  description: string;
  send_email_notification?: boolean;
  email_sent_at?: string | null;
  document_file?: string | null;
  client_name?: string;
  matter_name?: string;
  created_at: string;
  updated_at: string;
}

export interface DownPayment {
  id: number;
  payment_number: string;
  client: number;
  matter: number;
  payment_date: string;
  amount: number;
  applied_amount: number;
  remaining_amount: number;
  status: DownPaymentStatus;
  description: string;
  send_email_notification?: boolean;
  email_sent_at?: string;
  document_file?: string;
  client_name?: string;
  matter_name?: string;
  created_at: string;
  updated_at: string;
}

// Application tracking types for the unified system
export interface UnifiedApplication {
  id: number;
  source_invoice: {
    id: number;
    invoice_number: string;
    invoice_type: 'credit_note' | 'downpayment';
  };
  target_invoice: {
    id: number;
    invoice_number: string;
  };
  amount_applied: number;
  applied_by: string;
  applied_date: string;
  notes?: string;
}

// Available items for application
export interface AvailableForApplication {
  id: number;
  invoice_number: string;
  invoice_type: 'credit_note' | 'downpayment';
  total: number;
  remaining_amount: number;
  issue_date: string;
}

// Legacy application tracking types
export interface InvoiceCreditNoteApplication {
  id: number;
  invoice: number;
  credit_note: number;
  amount_applied: number;
  applied_at: string;
  applied_by: number;
  notes?: string;
}

export interface InvoiceDownPaymentApplication {
  id: number;
  invoice: number;
  down_payment: number;
  amount_applied: number;
  applied_at: string;
  applied_by: number;
  notes?: string;
}

export interface ApplicationHistory {
  id: number;
  type: 'credit_note' | 'down_payment';
  document_number: string;
  amount_applied: number;
  applied_at: string;
  applied_by: number;
  applied_by_name: string;
  notes?: string;
}

// Email notification types
export interface EmailRecipient {
  email: string;
  name: string;
  type: 'TO' | 'CC' | 'BCC';
}

export interface InvoiceRecipientsResponse {
  invoice_id: number;
  invoice_number: string;
  recipients: EmailRecipient[];
}

export interface EmailFormData {
  message: string;
  additional_recipients: string[];
  cc: string[];
  bcc: string[];
}

export interface EmailTemplate {
  subject: string;
  body: string;
  variables: string[];
}

// Status enums
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'partly_paid' | 'overdue' | 'cancelled';
export type CreditNoteStatus = 'draft' | 'sent' | 'applied' | 'cancelled';
export type DownPaymentStatus = 'draft' | 'sent' | 'paid' | 'applied' | 'cancelled';

// Unified filter types
export interface UnifiedInvoiceFilters {
  type?: 'invoice' | 'credit_note' | 'downpayment';
  search?: string;
  client?: number;
  matter?: number;
  status?: string; // Can be any status from any type
  from_date?: string;
  to_date?: string;
  // UI helper fields
  clientId?: number;
  matterId?: number;
  dateFrom?: Date;
  dateTo?: Date;
  ordering?: string;
}

// Create requests for unified API
export interface CreateUnifiedInvoiceRequest {
  matter_id: number;
  invoice_type: 'invoice';
  timesheet_ids?: number[];
  cost_ids?: number[];
  amount?: string; // For fixed amount invoices
  description?: string;
  issue_date: string;
  due_days?: number;
  send_email_notification?: boolean;
  client_id?: number;
  deduct_from_next_invoice?: boolean;
  overrides?: InvoiceOverrides;
}

export interface CreateUnifiedCreditNoteRequest {
  matter_id: number;
  invoice_type: 'credit_note';
  original_invoice_id?: number;
  credit_reason: string;
  amount: string;
  description: string;
  issue_date: string;
  send_email_notification?: boolean;
  client_id?: number;
  overrides?: InvoiceOverrides;
}

export interface CreateUnifiedDownPaymentRequest {
  matter_id: number;
  invoice_type: 'downpayment';
  amount: string;
  description: string;
  issue_date: string;
  due_days?: number;
  send_email_notification?: boolean;
  client_id?: number;
  overrides?: InvoiceOverrides;
}

// Specialized creation requests
export interface CreateFromTimesheetsRequest {
  matter_id: number;
  timesheet_ids: number[];
  cost_ids?: number[];
  issue_date: string;
  due_days?: number;
  send_email_notification?: boolean;
  client_id?: number;
  overrides?: InvoiceOverrides;
  // Discount & adjustment fields
  discount_type?: 'none' | 'percentage' | 'fixed' | 'both';
  discount_percentage?: number;
  discount_amount?: number;
  discount_description?: string;
  show_discount_on_invoice?: boolean;
  flat_fee_adjustment?: number;
  flat_fee_description?: string;
  show_flat_fee_on_invoice?: boolean;
  hour_adjustment?: number;
  rate_adjustment?: number;
  hour_adjustment_description?: string;
  show_hour_adjustment_on_invoice?: boolean;
  target_amount?: number | null;
  fixed_deduction_amount?: number;
  fixed_deduction_description?: string;
  show_fixed_deduction_on_invoice?: boolean;
  detail_level?: 'short' | 'summary' | 'detailed' | '';
}

export interface CreateStandaloneInvoiceRequest {
  matter_id: number;
  amount: string;
  description: string;
  issue_date: string;
  due_days?: number;
  send_email_notification?: boolean;
  client_id?: number;
  deduct_from_next_invoice?: boolean;
  overrides?: InvoiceOverrides;
}

// Status update request
export interface UpdateStatusRequest {
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'applied';
}

// Apply to invoice request (unified API)
export interface ApplyToInvoiceRequest {
  target_invoice_id: number;
  amount?: string;
  notes?: string;
}

export const CREDIT_REASONS = [
  { value: 'invoice_cancelled', label: 'Invoice Cancelled' },
  { value: 'billing_error', label: 'Billing Error' },
  { value: 'service_not_provided', label: 'Service Not Provided' },
  { value: 'client_dispute', label: 'Client Dispute' },
  { value: 'refund_request', label: 'Refund Request' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'discount', label: 'Discount' },
  { value: 'error', label: 'Error Correction' },
  { value: 'refund', label: 'Refund' },
  { value: 'other', label: 'Other' },
] as const;

// Override keys supported by the backend
export interface InvoiceOverrides {
  subtotal?: string;
  service_charge?: string;
  fee_split_percentage?: number;
  fee_split_amount?: string;
  vat_type?: 'none' | 'standard' | 'reduced' | 'zero' | 'exempt';
  vat_rate?: string;
  vat_amount?: string;
  total?: string;
  due_date?: string;
  status?: string;
}

export const VAT_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'standard', label: 'Standard' },
  { value: 'reduced', label: 'Reduced' },
  { value: 'zero', label: 'Zero' },
  { value: 'exempt', label: 'Exempt' },
] as const;

export const INVOICE_DETAIL_LEVELS = [
  { value: 'short', label: 'Short', description: 'Invoice' },
  { value: 'summary', label: 'Summary', description: 'Invoice + Summary' },
  { value: 'detailed', label: 'Detailed', description: 'Invoice + Timesheets' },
] as const;

export const DISCOUNT_TYPES = [
  { value: 'none', label: 'No Discount' },
  { value: 'percentage', label: 'Percentage Discount' },
  { value: 'fixed', label: 'Fixed Amount Discount' },
  { value: 'both', label: 'Percentage + Fixed' },
] as const;
export interface InvoiceFilters {
  search?: string;
  client?: number;
  matter?: number;
  status?: InvoiceStatus;
  from_date?: string;
  to_date?: string;
}

export interface CreditNoteFilters {
  search?: string;
  client?: number;
  matter?: number;
  status?: CreditNoteStatus;
  reason?: string;
  from_date?: string;
  to_date?: string;
}

export interface DownPaymentFilters {
  search?: string;
  client?: number;
  matter?: number;
  status?: DownPaymentStatus;
  from_date?: string;
  to_date?: string;
}

// API Response types
export interface PaginatedResponse<T> {
  count: number;
  next?: string;
  previous?: string;
  results: T[];
}

export interface InvoiceSummary {
  total_count: number;
  draft_count: number;
  sent_count: number;
  paid_count: number;
  overdue_count: number;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
}

// Enhanced timesheet and expense types
export interface Timesheet {
  id: number;
  matter: number;
  user: number;
  date: string;
  description: string;
  billable_hours: number;
  fee: number;
  billable: boolean;
  invoiced: boolean;
  created_at: string;
  updated_at: string;
}

export interface Expense {
  id: number;
  matter: number;
  date: string;
  category: string;
  description: string;
  notes?: string;
  amount: number;
  billable: boolean;
  invoiced: boolean;
  proof?: string;
  created_at: string;
  updated_at: string;
}

// Enhanced matter type
export interface Matter {
  id: number;
  name: string;
  group_name?: string;
  uninvoiced_amount?: number;
  uninvoiced_expense_amount?: number;
  status: 'active' | 'inactive';
  billing_rates: {
    user: number;
    amount: number;
  }[];
  fee_sharing_configs?: import('@/types/fee-sharing').FeeSharingConfig[];
  budget?: number;
  billable: boolean;
  email_on_budget_hit: boolean;
  rounding: boolean;
  rounding_type: 'up' | 'down' | 'nearest';
  rounding_amount: number;
  default_invoice_detail_level?: '' | 'short' | 'summary' | 'detailed';
  created_at: string;
  updated_at: string;
}

// Document choices
export interface DocumentChoices {
  status_choices: Array<{ value: string; label: string }>;
  reason_choices?: Array<{ value: string; label: string }>;
}

// Legacy Application request types (kept for backwards compat)
// Use the unified ApplyToInvoiceRequest above instead

export interface CreateInvoiceRequest {
  matter_id: number;
  timesheet_ids: number[];
  cost_ids: number[];
  send_email_notification?: boolean;
  notes?: string;
}

export interface CreateCreditNoteRequest {
  client: number;
  matter: number;
  amount: number;
  reason: string;
  description: string;
  send_email_notification?: boolean;
}

export interface CreateDownPaymentRequest {
  client: number;
  matter: number;
  amount: number;
  description: string;
  send_email_notification?: boolean;
}

// Timesheet editing interface
export interface TimesheetEditData {
  id: number;
  date: string;
  description: string;
  billable_hours: number;
  user: number;
  matter: number;
  fee: number;
  billable: boolean;
}

// Fee-sharing split preview types
export interface StandaloneDeductedInvoice {
  invoice_id: number;
  invoice_number: string;
  amount: number;
}

export interface SplitPreviewParticipant {
  client_id: number;
  client_name: string;
  fee_sharing_type: 'percentage' | 'fixed_amount';
  percentage_share: number;
  service_charge: number;
  subtotal: number;
  vat_type: string;
  vat_rate: number;
  vat_amount: number;
  total: number;
  detail_level: string;
  invoice_detail_level_override: string;
  fixed_amount: number | null;
  fixed_amount_remaining: number | null;
  is_exhausted: boolean;
  standalone_deduction: number;
  standalone_deducted_invoices: StandaloneDeductedInvoice[];
}

export interface SplitPreview {
  matter_id: number;
  matter_name: string;
  raw_timesheet_total: number;
  raw_costs_total: number;
  raw_total: number;
  discount_amount: number;
  flat_fee_adjustment: number;
  hour_adjustment_amount: number;
  fixed_deduction: number;
  standalone_deduction: number;
  adjusted_total: number;
  phase: string;
  participants: SplitPreviewParticipant[];
}
